/**
 * FEAT-058 — SendForReviewModal.
 *
 * Two independent send paths, same recipient list:
 *   - "Send via CCM": POST /pieces/:id/send-for-review — the real branded
 *     email (logo, palette, cover image), sent server-side via Resend.
 *   - "Send from my email": a mailto: link — opens the founder's own mail
 *     client with a plain-text excerpt. No backend call, no branding
 *     (mailto can't carry HTML), but works with zero setup and sends from
 *     the founder's real inbox.
 *
 * Same rev-* modal shell as RevisionModal (see styles.css) — Escape/overlay
 * click to cancel, same header/body/footer structure.
 */
import { useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { useApi, ApiError } from "@/lib/api";
import {
  summariseRecipients,
  isValidEmail,
  stripHtmlToText,
  buildMailtoHref,
} from "@/lib/review-email";

interface Props {
  pieceId: number;
  pieceTitle: string;
  /** Current draft HTML (for the mailto path's plain-text excerpt only — the CCM send re-derives its own HTML server-side). */
  draftHtml: string | null;
  onCancel: () => void;
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.body) as { message?: string };
      if (parsed.message) return parsed.message;
    } catch {
      // body wasn't JSON — fall through to err.message
    }
    return err.message;
  }
  return err instanceof Error ? err.message : "Send failed";
}

export function SendForReviewModal({ pieceId, pieceTitle, draftHtml, onCancel }: Props) {
  const api = useApi();
  const { user } = useUser();
  const [recipientsText, setRecipientsText] = useState("");
  // Pre-filled with the logged-in user's email — editable, so a reply lands
  // wherever the founder wants it, not just their own login address.
  const [replyTo, setReplyTo] = useState(() => user?.primaryEmailAddress?.emailAddress ?? "");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string[] | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onCancel]);

  const rcpt = summariseRecipients(recipientsText);
  const recipients = rcpt.all;
  const badEntries = rcpt.invalid;
  const replyToTrimmed = replyTo.trim();
  const replyToValid = replyToTrimmed === "" || isValidEmail(replyToTrimmed);
  const canSend = rcpt.ready && replyToValid && !sending;

  const subject = `Draft for review: ${pieceTitle.trim() || "Untitled"}`;

  async function sendViaCcm() {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      await api(`/pieces/${pieceId}/send-for-review`, {
        method: "POST",
        body: JSON.stringify({
          to: recipients,
          ...(replyToTrimmed ? { reply_to: replyToTrimmed } : {}),
        }),
      });
      setSentTo(recipients);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSending(false);
    }
  }

  function sendFromMyEmail() {
    if (!rcpt.ready) return;
    const body = draftHtml
      ? stripHtmlToText(draftHtml)
      : "(No draft content available — the piece has no draft yet.)";
    const href = buildMailtoHref({ to: recipients, subject, body });
    window.location.href = href;
  }

  return (
    <div
      className="rev-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="rev-modal" role="dialog" aria-labelledby="review-send-title" style={{ maxWidth: 520 }}>
        <header className="rev-head">
          <div className="rev-head-l">
            <div className="rev-eyebrow">Draft review</div>
            <h2 id="review-send-title" className="rev-title">
              Send for feedback
            </h2>
          </div>
          <button className="rev-x" onClick={onCancel} aria-label="Close">
            ×
          </button>
        </header>

        <div className="rev-body">
          {sentTo ? (
            <p className="rev-lede">
              Sent to <strong>{sentTo.join(", ")}</strong>.
            </p>
          ) : (
            <>
              {/* S-373 — the handoff has this modal lead with what it is
                  NOT. The fear it answers is "will this approve the piece
                  or hand it on?", and answering that first is what makes
                  the rest readable. */}
              <p className="rev-lede">
                <strong>This does not move the piece.</strong> It emails the
                current draft out for comment — the gate stays exactly where it
                is, and nothing about the piece changes.
              </p>

              <div className="rev-section">
                <div className="rev-section-head">
                  <span className="rev-section-label">Recipient email(s)</span>
                  <span className={`rev-section-aside ${badEntries.length > 0 ? "is-warn" : ""}`}>
                    {badEntries.length > 0
                      ? `Not a valid email: ${badEntries.join(", ")}`
                      : rcpt.validCount > 0
                      ? `${rcpt.validCount} recipient${rcpt.validCount === 1 ? "" : "s"}`
                      : "Comma or newline separated"}
                  </span>
                </div>
                <input
                  type="text"
                  className="rev-textarea"
                  style={{ minHeight: "unset" }}
                  placeholder="client@example.com"
                  value={recipientsText}
                  onChange={(e) => setRecipientsText(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="rev-section">
                <div className="rev-section-head">
                  <span className="rev-section-label">Replies go to</span>
                  <span className={`rev-section-aside ${!replyToValid ? "is-warn" : ""}`}>
                    {!replyToValid
                      ? "Not a valid email"
                      : "Only used by \"Send via CCM\" — defaults to your login email"}
                  </span>
                </div>
                <input
                  type="text"
                  className="rev-textarea"
                  style={{ minHeight: "unset" }}
                  placeholder="you@example.com"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                />
              </div>

              {/* S-373 — both paths side by side with the tradeoff stated.
                  They were two unlabelled footer buttons whose difference
                  lived in a title attribute, which is invisible until you
                  hover the one you were not going to click. */}
              <div className="rev-section">
                <div className="rev-section-head">
                  <span className="rev-section-label">How to send</span>
                </div>
                <div className="send-paths">
                  <div className="send-path">
                    <span className="send-path-name">Branded email</span>
                    <p className="send-path-trade">
                      Sent by the platform with the publication's logo, palette
                      and cover image. Replies go to the address above.
                    </p>
                    <button
                      className="rev-btn rev-btn-primary send-path-go"
                      disabled={!canSend}
                      onClick={() => void sendViaCcm()}
                    >
                      {sending ? "Sending…" : "Send via CCM"}
                    </button>
                  </div>
                  <div className="send-path">
                    <span className="send-path-name">Your own mail client</span>
                    <p className="send-path-trade">
                      Opens a plain-text copy in your mail app. No setup and it
                      comes from your real inbox, but no branding — mail links
                      cannot carry HTML.
                    </p>
                    <button
                      className="rev-btn rev-btn-ghost send-path-go"
                      disabled={!rcpt.ready}
                      onClick={sendFromMyEmail}
                    >
                      Send from my email
                    </button>
                  </div>
                </div>
              </div>

              {error && (
                <div className="login-error" style={{ marginTop: 4 }}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <footer className="rev-foot">
          <div className="rev-foot-l" />
          <div className="rev-actions">
            <button className="rev-btn rev-btn-ghost" onClick={onCancel}>
              {sentTo ? "Close" : "Cancel"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
